import { useEffect, useMemo, useState } from 'react'
import { applicationRepository } from '@/core/storage/db'
import type { JobApplication, PipelineStage } from '@/core/types'
import {
  ArrowRight,
  Briefcase,
  Calendar,
  CheckCircle,
  Clock,
  Plus,
  Puzzle,
  Sparkles,
  Target,
  TrendingUp,
  User,
  XCircle,
} from 'lucide-react'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthProvider'

const STAGES: PipelineStage[] = ['applied', 'interviewing', 'offer', 'rejected', 'closed']

const stageConfig: Record<
  PipelineStage,
  {
    label: string
    icon: typeof Briefcase
    tint: string
    badge: string
    iconWrap: string
    iconColor: string
  }
> = {
  applied: {
    label: 'Applied',
    icon: Briefcase,
    tint: 'bg-primary/5 border-primary/15 dark:bg-primary/10 dark:border-primary/20',
    badge: 'bg-primary/10 text-primary border-primary/20 dark:bg-primary/15',
    iconWrap: 'bg-primary/10 dark:bg-primary/15',
    iconColor: 'text-primary',
  },
  interviewing: {
    label: 'Interviewing',
    icon: Clock,
    tint: 'bg-amber-500/5 border-amber-500/15 dark:bg-amber-400/10 dark:border-amber-400/20',
    badge: 'bg-amber-500/10 text-amber-700 border-amber-500/20 dark:bg-amber-400/15 dark:text-amber-300 dark:border-amber-400/25',
    iconWrap: 'bg-amber-500/10 dark:bg-amber-400/15',
    iconColor: 'text-amber-700 dark:text-amber-300',
  },
  offer: {
    label: 'Offer',
    icon: CheckCircle,
    tint: 'bg-emerald-500/5 border-emerald-500/15 dark:bg-emerald-400/10 dark:border-emerald-400/20',
    badge: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:bg-emerald-400/15 dark:text-emerald-300 dark:border-emerald-400/25',
    iconWrap: 'bg-emerald-500/10 dark:bg-emerald-400/15',
    iconColor: 'text-emerald-700 dark:text-emerald-300',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    tint: 'bg-rose-500/5 border-rose-500/15 dark:bg-rose-400/10 dark:border-rose-400/20',
    badge: 'bg-rose-500/10 text-rose-700 border-rose-500/20 dark:bg-rose-400/15 dark:text-rose-300 dark:border-rose-400/25',
    iconWrap: 'bg-rose-500/10 dark:bg-rose-400/15',
    iconColor: 'text-rose-700 dark:text-rose-300',
  },
  closed: {
    label: 'Closed',
    icon: XCircle,
    tint: 'bg-muted border-border',
    badge: 'bg-muted text-muted-foreground border-border',
    iconWrap: 'bg-muted',
    iconColor: 'text-muted-foreground',
  },
}

const statCardTints = {
  primary: 'bg-primary/5 border-primary/15 dark:bg-primary/10 dark:border-primary/20',
  emerald: 'bg-emerald-500/5 border-emerald-500/15 dark:bg-emerald-400/10 dark:border-emerald-400/20',
  amber: 'bg-amber-500/5 border-amber-500/15 dark:bg-amber-400/10 dark:border-amber-400/20',
  violet: 'bg-violet-500/5 border-violet-500/15 dark:bg-violet-400/10 dark:border-violet-400/20',
} as const

const statIconTints = {
  primary: 'bg-primary/10 text-primary dark:bg-primary/15',
  emerald: 'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300',
  amber: 'bg-amber-500/10 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300',
  violet: 'bg-violet-500/10 text-violet-700 dark:bg-violet-400/15 dark:text-violet-300',
} as const

const surfaceClass = 'rounded-2xl border border-border bg-card/95 shadow-sm'

export function Dashboard() {
  const [applications, setApplications] = useState<JobApplication[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { profile } = useAuth()

  useEffect(() => {
    loadApplications()
  }, [])

  async function loadApplications() {
    try {
      const apps = await applicationRepository.getAll()
      setApplications(apps)
    } catch (error) {
      console.error('Failed to load applications:', error)
    } finally {
      setLoading(false)
    }
  }

  const stats = useMemo(() => {
    const active = applications.filter(a => a.status === 'active')
    const now = new Date()
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    const today = now.toISOString().split('T')[0]

    const byStage: Record<PipelineStage, number> = {
      applied: 0,
      interviewing: 0,
      offer: 0,
      rejected: 0,
      closed: 0,
    }

    active.forEach(app => {
      byStage[app.stage]++
    })

    const totalActive = active.length
    const responded = byStage.interviewing + byStage.offer + byStage.rejected + byStage.closed
    const responseRate = totalActive > 0 ? Math.round((responded / totalActive) * 100) : 0
    const thisWeek = active.filter(app => app.appliedDate >= weekAgo.toISOString().split('T')[0]).length
    const thisMonth = active.filter(app => app.appliedDate >= monthAgo.toISOString().split('T')[0]).length
    const upcomingFollowUps = active.filter(app => app.followUpDate && app.followUpDate <= today)
    const recentApps = [...active]
      .sort((a, b) => b.appliedDate.localeCompare(a.appliedDate))
      .slice(0, 5)

    return {
      totalActive,
      byStage,
      responseRate,
      thisWeek,
      thisMonth,
      upcomingFollowUps,
      recentApps,
    }
  }, [applications])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  const firstName = profile?.displayName?.trim().split(' ')[0] ?? ''
  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'
  const welcomeTitle = firstName ? `${greeting}, ${firstName}` : greeting
  const isEmpty = applications.length === 0

  const primaryAction = profile?.displayName
    ? { label: 'Add your first application', icon: Plus, path: '/app/applications' }
    : { label: 'Set up your profile', icon: User, path: '/app/profile' }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">{welcomeTitle}</h2>
          <p className="text-sm text-muted-foreground">
            {isEmpty
              ? 'Set up your tracker once, then this dashboard turns into your search command center.'
              : 'Here’s the current state of your job search.'}
          </p>
        </div>

        <div className="flex items-center gap-2 self-start">
          <QuickAction
            icon={primaryAction.icon}
            label={primaryAction.label}
            onClick={() => navigate(primaryAction.path)}
            primary
          />
          {!isEmpty && (
            <>
              <QuickAction icon={User} label="Profile" onClick={() => navigate('/app/profile')} />
              <QuickAction icon={Puzzle} label="Extension" onClick={() => navigate('/app/settings')} />
            </>
          )}
        </div>
      </div>

      {isEmpty ? (
        <EmptyDashboardState
          hasProfile={Boolean(profile?.displayName)}
          onNavigate={navigate}
        />
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Total Active"
              value={stats.totalActive}
              hint="Open applications being tracked right now"
              icon={<Briefcase className="h-4 w-4" />}
              tone="primary"
            />
            <StatCard
              label="This Week"
              value={stats.thisWeek}
              hint={`${stats.thisMonth} added in the last 30 days`}
              icon={<Calendar className="h-4 w-4" />}
              tone="emerald"
            />
            <StatCard
              label="Interviews"
              value={stats.byStage.interviewing}
              hint="Applications currently in interview loops"
              icon={<Clock className="h-4 w-4" />}
              tone="amber"
            />
            <StatCard
              label="Response Rate"
              value={`${stats.responseRate}%`}
              hint="Share of active applications with a response"
              icon={<TrendingUp className="h-4 w-4" />}
              tone="violet"
            />
          </div>

          {stats.totalActive < 3 && (
            <GettingStartedChecklist
              hasProfile={Boolean(profile?.displayName)}
              hasApplications={stats.totalActive > 0}
              onNavigate={navigate}
              compact
            />
          )}

          <section className={`${surfaceClass} p-5 sm:p-6`}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Pipeline Overview
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">Where your active applications stand.</p>
              </div>
              <button
                onClick={() => navigate('/app/applications')}
                className="inline-flex items-center gap-1 text-sm font-medium text-primary transition-colors hover:text-primary/80"
              >
                View all <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              {STAGES.map(stage => {
                const config = stageConfig[stage]
                const Icon = config.icon
                const count = stats.byStage[stage]

                return (
                  <button
                    key={stage}
                    type="button"
                    onClick={() => navigate('/app/applications')}
                    className={`flex min-h-[110px] flex-col items-start gap-3 rounded-2xl border p-4 text-left transition-colors hover:bg-accent/60 ${config.tint}`}
                  >
                    <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${config.iconWrap}`}>
                      <Icon className={`h-4 w-4 ${config.iconColor}`} />
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-foreground">{count}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{config.label}</p>
                    </div>
                  </button>
                )
              })}
            </div>
          </section>

          <div className="grid gap-6 xl:grid-cols-2">
            <section className={`${surfaceClass} p-5 sm:p-6`}>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Follow-ups Due
              </h3>

              {stats.upcomingFollowUps.length === 0 ? (
                <EmptyPanel
                  icon={<CheckCircle className="h-5 w-5" />}
                  iconTone="emerald"
                  title="You’re all caught up"
                  description="Follow-up reminders will show up here when any active application needs attention."
                />
              ) : (
                <div className="space-y-2">
                  {stats.upcomingFollowUps.slice(0, 5).map(app => (
                    <button
                      key={app.id}
                      type="button"
                      className="group flex w-full items-center justify-between rounded-xl border border-transparent px-3 py-3 text-left transition-colors hover:border-border hover:bg-accent/60"
                      onClick={() => navigate('/app/applications')}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground group-hover:text-primary">
                          {app.company}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">{app.position}</p>
                      </div>
                      <span className="ml-4 rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-700 dark:border-amber-400/25 dark:bg-amber-400/15 dark:text-amber-300">
                        {app.followUpDate && format(new Date(app.followUpDate), 'MMM d')}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className={`${surfaceClass} p-5 sm:p-6`}>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Recent Activity
              </h3>

              {stats.recentApps.length === 0 ? (
                <EmptyPanel
                  icon={<Briefcase className="h-5 w-5" />}
                  iconTone="primary"
                  title="No recent activity yet"
                  description="Once you add applications, the latest changes and stage updates will appear here."
                  action={{
                    label: 'Add application',
                    onClick: () => navigate('/app/applications'),
                  }}
                />
              ) : (
                <div className="space-y-2">
                  {stats.recentApps.map(app => (
                    <button
                      key={app.id}
                      type="button"
                      className="group flex w-full items-center justify-between rounded-xl border border-transparent px-3 py-3 text-left transition-colors hover:border-border hover:bg-accent/60"
                      onClick={() => navigate('/app/applications')}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground group-hover:text-primary">
                          {app.company}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">{app.position}</p>
                      </div>

                      <div className="ml-4 flex flex-col items-end gap-1">
                        <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${stageConfig[app.stage].badge}`}>
                          {stageConfig[app.stage].label}
                        </span>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(app.appliedDate), 'MMM d')}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  )
}

function EmptyDashboardState({
  hasProfile,
  onNavigate,
}: {
  hasProfile: boolean
  onNavigate: (path: string) => void
}) {
  const nextStep = hasProfile
    ? { label: 'Add your first application', path: '/app/applications', icon: Plus }
    : { label: 'Set up your profile', path: '/app/profile', icon: User }

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.9fr)]">
      <section className={`${surfaceClass} overflow-hidden p-6 sm:p-8`}>
        <div className="max-w-2xl space-y-5">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-primary/5 px-3 py-1 text-xs font-medium text-primary dark:border-primary/20 dark:bg-primary/10">
            <Sparkles className="h-3.5 w-3.5" />
            New dashboard setup
          </div>

          <div className="space-y-3">
            <h3 className="text-3xl font-semibold tracking-tight text-foreground">
              This space fills in as soon as you start tracking applications.
            </h3>
            <p className="max-w-xl text-sm leading-6 text-muted-foreground sm:text-base">
              Instead of showing a wall of zeros, JobFlow should help you get oriented. Finish setup,
              add your first application, and this page will turn into a live view of your pipeline,
              response rate, interviews, and follow-ups.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <QuickAction
              icon={nextStep.icon}
              label={nextStep.label}
              onClick={() => onNavigate(nextStep.path)}
              primary
            />
            <QuickAction icon={Puzzle} label="Extension setup" onClick={() => onNavigate('/app/settings')} />
          </div>

          <div className="grid gap-3 pt-2 sm:grid-cols-3">
            <PreviewMetricCard
              title="Pipeline health"
              description="See how many applications are applied, interviewing, offered, or closed."
              icon={<Briefcase className="h-4 w-4" />}
              tone="primary"
            />
            <PreviewMetricCard
              title="Response rate"
              description="Spot whether applications are turning into recruiter replies and interviews."
              icon={<TrendingUp className="h-4 w-4" />}
              tone="violet"
            />
            <PreviewMetricCard
              title="Follow-up timing"
              description="Catch which companies need a nudge before opportunities go cold."
              icon={<Calendar className="h-4 w-4" />}
              tone="emerald"
            />
          </div>
        </div>
      </section>

      <GettingStartedChecklist
        hasProfile={hasProfile}
        hasApplications={false}
        onNavigate={onNavigate}
      />
    </div>
  )
}

function StatCard({
  label,
  value,
  hint,
  icon,
  tone,
}: {
  label: string
  value: number | string
  hint: string
  icon: React.ReactNode
  tone: keyof typeof statCardTints
}) {
  return (
    <section className={`${surfaceClass} ${statCardTints[tone]} p-4`}>
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
          <p className="text-2xl font-semibold leading-none text-foreground">{value}</p>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${statIconTints[tone]}`}>
          {icon}
        </div>
      </div>
    </section>
  )
}

function PreviewMetricCard({
  title,
  description,
  icon,
  tone,
}: {
  title: string
  description: string
  icon: React.ReactNode
  tone: keyof typeof statCardTints
}) {
  return (
    <div className={`${surfaceClass} ${statCardTints[tone]} p-4`}>
      <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-xl ${statIconTints[tone]}`}>
        {icon}
      </div>
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="mt-1 text-xs leading-5 text-muted-foreground">{description}</p>
    </div>
  )
}

function QuickAction({
  icon: Icon,
  label,
  onClick,
  primary,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  onClick: () => void
  primary?: boolean
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-medium transition-colors ${
        primary
          ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
          : 'border border-border bg-card text-foreground hover:bg-accent'
      }`}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </button>
  )
}

function EmptyPanel({
  icon,
  iconTone,
  title,
  description,
  action,
}: {
  icon: React.ReactNode
  iconTone: keyof typeof statCardTints
  title: string
  description: string
  action?: { label: string; onClick: () => void }
}) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-border bg-muted/30 px-6 py-10 text-center">
      <div className={`mb-3 flex h-11 w-11 items-center justify-center rounded-xl ${statIconTints[iconTone]}`}>
        {icon}
      </div>
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="mt-1 max-w-sm text-xs leading-5 text-muted-foreground">{description}</p>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="mt-4 text-sm font-medium text-primary transition-colors hover:text-primary/80"
        >
          {action.label}
        </button>
      )}
    </div>
  )
}

function GettingStartedChecklist({
  hasProfile,
  hasApplications,
  onNavigate,
  compact = false,
}: {
  hasProfile: boolean
  hasApplications: boolean
  onNavigate: (path: string) => void
  compact?: boolean
}) {
  const steps = [
    {
      label: 'Set up your profile',
      description: 'Add your basic info so every application starts from a saved foundation.',
      done: hasProfile,
      action: () => onNavigate('/app/profile'),
      icon: User,
    },
    {
      label: 'Install browser extension',
      description: 'Turn on autofill so you can save jobs while you apply.',
      done: false,
      action: () => onNavigate('/app/settings'),
      icon: Puzzle,
    },
    {
      label: 'Track your first application',
      description: 'Once one application is in, the dashboard starts showing metrics and activity.',
      done: hasApplications,
      action: () => onNavigate('/app/applications'),
      icon: Target,
    },
  ]

  const completed = steps.filter(step => step.done).length

  return (
    <section className={`${surfaceClass} bg-primary/5 border-primary/15 dark:bg-primary/10 dark:border-primary/20 p-6`}>
      <div className={`flex ${compact ? 'flex-col gap-5' : 'h-full flex-col gap-6'}`}>
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
            <Sparkles className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-base font-semibold text-foreground">Getting Started</h3>
                <p className="text-sm text-muted-foreground">
                  Complete the basics once. The tracker gets a lot more useful immediately after that.
                </p>
              </div>
              <span className="text-sm font-medium text-muted-foreground">
                {completed}/{steps.length} complete
              </span>
            </div>

            <div className="mt-4 max-w-[600px]">
              <div className="h-2 overflow-hidden rounded-full bg-primary/10 dark:bg-primary/15">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-500"
                  style={{ width: `${(completed / steps.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {steps.map(step => (
            <button
              key={step.label}
              type="button"
              onClick={step.action}
              className="flex w-full items-start gap-3 rounded-xl border border-transparent px-1 py-1 text-left transition-colors hover:border-border hover:bg-background/40"
            >
              <div
                className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border ${
                  step.done
                    ? 'border-emerald-500 bg-emerald-500 text-white dark:border-emerald-400 dark:bg-emerald-400 dark:text-emerald-950'
                    : 'border-border bg-background text-muted-foreground'
                }`}
              >
                {step.done ? <CheckCircle className="h-4 w-4" /> : <step.icon className="h-4 w-4" />}
              </div>

              <div className="min-w-0">
                <p className={`text-sm font-medium ${step.done ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                  {step.label}
                </p>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">{step.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  )
}
